/* eslint-disable react/jsx-filename-extension */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * CountUp component
 * Props:
 *  - end: number|string (final value)
 *  - duration: number (ms)
 *  - separator: string (thousands separator)
 *  - prefix: string
 *  - suffix: string
 *  - startOnVisible: boolean
 *  - className: string
 */
export default function CountUp({
  end,
  duration,
  separator,
  prefix,
  suffix,
  startOnVisible,
  className,
}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const startAnimation = () => {
      if (started.current) return;
      started.current = true;

      const start = performance.now();
      const from = 0;
      const to = Number(end) || 0;
      const dur = Math.max(200, duration);

      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        // easeOutCubic using exponentiation operator
        const eased = 1 - (1 - t) ** 3;
        const current = Math.round(from + (to - from) * eased);
        setValue(current);
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          setValue(to);
        }
      };

      requestAnimationFrame(step);
    };

    if (!startOnVisible) {
      startAnimation();
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) startAnimation();
        });
      },
      { threshold: 0.25 },
    );

    io.observe(el);

    return () => {
      io.disconnect();
    };
  }, [end, duration, startOnVisible]);

  const formatted = String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

CountUp.propTypes = {
  end: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  duration: PropTypes.number,
  separator: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  startOnVisible: PropTypes.bool,
  className: PropTypes.string,
};

CountUp.defaultProps = {
  end: 0,
  duration: 1600,
  separator: ',',
  prefix: '',
  suffix: '',
  startOnVisible: true,
  className: '',
};
